import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  ArticleSources,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['aca-health-insurance-early-retirement'];

export default function AcaHealthInsuranceEarlyRetirementPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['tax-aware-fire-planning', 'what-if-i-retire-earlier']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'ACA planning is one of the biggest non-portfolio variables in early retirement because premiums and out-of-pocket exposure can change materially with household income.',
          'The key input is projected household MAGI for the full calendar year, not just investment spending, cash withdrawals, or the income earned after leaving work.',
          'A competent FIRE plan should model the bridge from employer coverage to Medicare, spouse and dependent coverage, Marketplace subsidies, Medicaid or CHIP interactions, and tax-year reconciliation risk.',
        ]}
      />

      <ArticleSection title="Health insurance is part of the early retirement number">
        <ArticleParagraph>
          Early retirement planning often focuses on portfolio size, withdrawal rate, and tax
          strategy. Health insurance deserves the same level of attention. A household retiring
          before Medicare eligibility may need to cover several years of premiums, deductibles,
          copays, prescriptions, and network tradeoffs before Medicare begins.
        </ArticleParagraph>
        <ArticleParagraph>
          The Affordable Care Act Marketplace can make that bridge workable, but it is not a
          flat monthly bill. The price a household actually pays can depend on ages, ZIP code,
          household size, tobacco use, plan metal level, the local benchmark Silver plan, and
          the household's projected modified adjusted gross income for the coverage year.
        </ArticleParagraph>
        <ArticleParagraph>
          That makes ACA planning a FIRE modeling problem, not just an insurance shopping task.
          A Roth conversion, taxable brokerage sale, consulting project, severance payment, or
          one-time bonus can change the same income number used to calculate Marketplace savings.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileScenarios}
        alt="Per Diem mobile scenario planning screen"
        caption="Health insurance belongs in scenario planning because the cost can change when income, family size, retirement timing, or Medicare timing changes."
        variant="phone"
      />

      <ArticleSection title="The bridge from employer coverage to Medicare">
        <ArticleParagraph>
          Most early retirees need a bridge strategy between the last month of employer coverage
          and the first month of Medicare. For many households that means comparing four paths:
          a Marketplace plan, COBRA, coverage through a spouse's employer plan, or retiree
          coverage from a former employer.
        </ArticleParagraph>
        <ArticleParagraph>
          Losing job-based coverage generally creates a Special Enrollment Period for the
          Marketplace. The important planning detail is timing: Marketplace coverage usually
          starts the first day of the month after job-based coverage ends, and the household
          normally has 60 days after losing job-based coverage to enroll.
        </ArticleParagraph>
        <ArticleParagraph>
          COBRA can be useful when continuity of doctors, treatment, or deductibles matters,
          but it is often expensive because the former employee usually pays the full premium
          plus an administrative charge. It can also interact awkwardly with Marketplace timing:
          if a household elects COBRA and later wants to leave it outside Open Enrollment, it
          may need another qualifying event unless COBRA is ending or the household is still
          inside the original loss-of-coverage window.
        </ArticleParagraph>
        <ArticleParagraph>
          The bridge ends differently for each person. Medicare eligibility generally begins at
          age 65, so a 58-year-old retiree married to a 62-year-old spouse may have two separate
          Medicare transition dates. Children may stay on a parent's plan until age 26, but their
          exact Marketplace treatment depends on tax dependency and whether they are covered on
          the application.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="Who counts in the ACA household">
        <ArticleParagraph>
          Marketplace household size usually starts with the tax filer, a legal spouse if married,
          and everyone the filer expects to claim as a tax dependent. That rule matters because
          household size determines the federal poverty level percentage used for subsidy
          eligibility.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'Spouses usually count in the Marketplace household even if only one spouse needs coverage.',
            'Tax dependents count even if they do not need Marketplace coverage.',
            'Dependent children can remain on a parent plan until age 26, subject to plan and state rules.',
            'A non-dependent child under 26 may be included only if the household wants to cover that child on the Marketplace plan.',
            'Children may qualify for Medicaid or CHIP even when parents use a Marketplace plan, so the family coverage answer can be split across programs.',
          ]}
        />
        <ArticleParagraph>
          This is one reason family FIRE planning is more complex than single-person planning.
          The model has to know who is in the tax household, who needs coverage, who ages into
          Medicare, who ages off parent coverage, and whether children are tax dependents in the
          relevant coverage year.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.addPartner}
        alt="Per Diem household planning screen"
        caption="ACA planning is household-sensitive: spouses, dependents, and children aging into or out of coverage can change the insurance path."
      />

      <ArticleSection title="MAGI is the income number that matters">
        <ArticleParagraph>
          Marketplace savings are based on modified adjusted gross income, usually abbreviated
          MAGI. For most households it is close to adjusted gross income, but the adjustment
          matters. Marketplace MAGI starts with AGI and adds items such as tax-exempt interest,
          non-taxable Social Security benefits, and untaxed foreign income. Supplemental Security
          Income is not included.
        </ArticleParagraph>
        <ArticleParagraph>
          For an early retiree, MAGI is often a controlled variable. Wages may fall after leaving
          work, but taxable portfolio income, realized capital gains, traditional IRA withdrawals,
          Roth conversions, part-time work, severance, and business income can all push MAGI up.
          Traditional IRA contributions, HSA contributions when eligible, and other above-the-line
          deductions may reduce AGI, which can also affect Marketplace MAGI.
        </ArticleParagraph>
        <ArticleParagraph>
          The mistake is to model ACA eligibility from spending alone. A household might spend
          $80,000, but only generate $45,000 of MAGI if it uses cash, basis from taxable accounts,
          and Roth principal. Another household might spend the same amount but generate $120,000
          of MAGI because it realizes gains or converts pre-tax retirement money.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="Income thresholds for 2026 coverage">
        <ArticleParagraph>
          For 2026 Marketplace savings, the planning baseline is the 2025 federal poverty level
          table for the household size. The Marketplace uses prior-year FPL amounts to calculate
          savings for the next coverage year, while Medicaid and CHIP checks use current-year
          income guidelines.
        </ArticleParagraph>
        <ArticleParagraph>
          In the 48 contiguous states and DC, the 2025 FPL amounts are $15,650 for one person,
          $21,150 for two, $26,650 for three, $32,150 for four, and $37,650 for five. Alaska
          and Hawaii use higher guidelines.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            '100% FPL is the lower Marketplace premium tax credit threshold in many cases: $15,650 for one person, $21,150 for two, $26,650 for three, and $32,150 for four.',
            '138% FPL is a key Medicaid expansion planning line: about $21,597 for one person, $29,187 for two, $36,777 for three, and $44,367 for four.',
            '250% FPL is important because cost-sharing reductions become a major plan-design question below this level: $39,125 for one person, $52,875 for two, $66,625 for three, and $80,375 for four.',
            '400% FPL matters under 2026 rules because Marketplace premium tax credit eligibility generally ends there: $62,600 for one person, $84,600 for two, $106,600 for three, and $128,600 for four.',
          ]}
        />
        <ArticleParagraph>
          These are not spending targets. They are MAGI planning boundaries. A FIRE plan that
          ignores the difference can make health insurance look either too expensive or too easy.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileBudget}
        alt="Per Diem mobile budget screen"
        caption="A health insurance model has to connect spending needs to taxable-income choices, not treat the premium as a fixed bill."
        variant="phone"
      />

      <ArticleSection title="Premium tax credits and the 400% FPL cliff">
        <ArticleParagraph>
          The premium tax credit lowers monthly Marketplace premiums. The Marketplace estimates
          the credit from household size, projected annual household income, and local plan prices.
          A household can take the credit in advance to reduce monthly premiums, wait and claim it
          at tax filing, or use only part of the advance credit.
        </ArticleParagraph>
        <ArticleParagraph>
          For 2026, the temporary expanded subsidy rules that removed the 400% FPL cliff are no
          longer the baseline rule. That means a household near 400% FPL should model the cliff
          explicitly unless Congress changes the law again. A small Roth conversion or capital
          gain above the line can have a large insurance-cost effect.
        </ArticleParagraph>
        <ArticleParagraph>
          This does not mean every early retiree should keep MAGI as low as possible. Sometimes
          it is rational to realize gains, fill a tax bracket, convert pre-tax retirement money,
          or accept consulting income. The point is that the insurance cost should be part of the
          marginal-rate calculation. For early retirees, the effective marginal rate can include
          federal tax, state tax, lost premium credits, lost cost-sharing reductions, and future
          Medicare-related effects.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="Cost-sharing reductions can matter more than the premium">
        <ArticleParagraph>
          Premiums are only one part of the decision. Cost-sharing reductions can lower deductibles,
          copays, coinsurance, and out-of-pocket maximums, but they are only available when an
          eligible household chooses a Silver plan.
        </ArticleParagraph>
        <ArticleParagraph>
          This matters because a low-premium Bronze plan is not always the cheapest plan in a
          bad health year. A household inside the cost-sharing reduction range may find that an
          enhanced Silver plan has a better total-risk profile than a cheaper Bronze plan with a
          much higher deductible. A high-income household that does not qualify for CSR may make
          a different tradeoff.
        </ArticleParagraph>
        <ArticleParagraph>
          A useful FIRE model should separate premium cost from expected health spending and
          worst-case out-of-pocket exposure. For a healthy household, expected cost may dominate.
          For a household with chronic prescriptions, planned surgery, pregnancy, therapy, or
          specialist care, network and out-of-pocket maximums may matter more than the premium.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="Spouses, kids, and split coverage">
        <ArticleParagraph>
          ACA planning is rarely one policy for one person. A household may need to evaluate one
          spouse on COBRA for continuity of care, another spouse on a Marketplace plan, children
          on CHIP, and later one spouse on Medicare while the younger spouse remains on a
          Marketplace plan.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'If one spouse still has an affordable employer plan available, that offer can block Marketplace subsidies for the person who has affordable coverage available.',
            'If employer family coverage is not affordable for other household members, those family members may still qualify for Marketplace savings under the family affordability rules.',
            'Children under 26 can often stay on a parent plan, but tax dependency and coverage choices affect how they are treated on a Marketplace application.',
            'A child turning 26 creates its own coverage transition and may need a separate plan before the parent reaches Medicare.',
            'A spouse reaching Medicare at 65 does not automatically solve coverage for a younger spouse or dependent children.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="Tax implications FIRE households should model">
        <ArticleParagraph>
          Advance premium tax credits are reconciled on the federal tax return using Form 8962.
          If the household receives too much advance credit, the excess can increase tax due or
          reduce the refund. For tax years after 2025, the IRS says there is no repayment cap for
          excess advance premium tax credit, so the downside of a bad income estimate is larger.
        </ArticleParagraph>
        <ArticleParagraph>
          That makes midyear updates important. A household should update the Marketplace when
          income, family size, address, employer coverage, marital status, or dependent status
          changes. FIRE households should pay special attention to capital gains, Roth conversions,
          Social Security decisions, severance, consulting income, and taxable retirement account
          withdrawals.
        </ArticleParagraph>
        <ArticleParagraph>
          Married households generally need to file jointly to qualify for premium tax credits,
          with limited exceptions. Filing status is therefore part of the insurance model, not a
          separate tax-prep afterthought.
        </ArticleParagraph>
        <ArticleParagraph>
          Medicare timing creates another tax planning edge case. Medicare income-related monthly
          adjustment amounts use tax return information from two years earlier, so a large Roth
          conversion or capital gain shortly before age 65 can affect the first Medicare premium
          years even after Marketplace coverage ends.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileAccounts}
        alt="Per Diem mobile accounts screen"
        caption="Insurance planning depends on where spending money comes from: cash, taxable gains, pre-tax withdrawals, Roth principal, and income can produce different MAGI outcomes."
        variant="phone"
      />

      <ArticleSection title="A practical early-retirement ACA checklist">
        <ArticleParagraph>
          A household evaluating early retirement should model ACA coverage year by year, person by
          person. The point is not to predict every premium perfectly. The point is to understand
          which variables drive the plan and which decisions create risk.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'List every person needing coverage and the month each person becomes Medicare-eligible or ages off a parent plan.',
            'Identify the coverage bridge for each person: Marketplace, COBRA, spouse employer plan, retiree coverage, Medicaid, CHIP, or Medicare.',
            'Estimate full-calendar-year MAGI, including income earned before retirement in the same tax year.',
            'Model premium tax credits, cost-sharing reductions, and the 400% FPL cliff for each year before Medicare.',
            'Separate premium cost, expected out-of-pocket cost, and worst-case out-of-pocket maximum.',
            'Stress test Roth conversions, capital gains, part-time work, and severance before assuming they are tax-efficient.',
            'Revisit the plan during Open Enrollment and after any major household or income change.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="What competence looks like in a FIRE planner">
        <ArticleParagraph>
          A weak plan treats health insurance as a flat inflation-adjusted expense until age 65.
          A stronger plan treats it as a linked system: household composition, tax filing status,
          projected MAGI, plan design, state Medicaid rules, children, spouse ages, Medicare dates,
          and tax strategy all affect the result.
        </ArticleParagraph>
        <ArticleParagraph>
          The hard part is not merely estimating a premium. The hard part is showing how a planning
          decision changes the insurance answer. If a household retires six months earlier, realizes
          a large capital gain, converts $40,000 to Roth, or moves to a different state, the health
          insurance plan may change. A FIRE planner should make that visible before the household
          makes the decision.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="Primary sources and review standard">
        <ArticleParagraph>
          This guide was reviewed against current federal Marketplace, tax, poverty-guideline,
          and Medicare guidance. Rules, plan availability, and dollar thresholds can change, so
          re-check the official sources and your state Marketplace before making a coverage or
          tax decision.
        </ArticleParagraph>
        <ArticleSources sources={article.sources ?? []} />
      </ArticleSection>
    </ArticleLayout>
  );
}
